import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The index page is served without cache headers, so a tab left open (or reopened from a bookmark) keeps running
 * the build it first loaded long after a newer one was deployed, and the desk reads a bug that was fixed hours ago.
 * This notice compares the bundle this tab runs against the one the server hands out now, and offers a reload.
 */

/** The hashed main bundle an index page points at, or null on a page that names none (the dev server, a test). */
export const bundleNameOf = (html: string): string | null =>
  html.match(/assets\/index-[A-Za-z0-9_-]+\.js/)?.[0] ?? null;

/** The hashed main bundle this document is running. */
export const currentBundleName = (doc: Document): string | null => {
  const script = Array.from(doc.querySelectorAll('script[src]')).find((element) =>
    /assets\/index-[A-Za-z0-9_-]+\.js/.test(element.getAttribute('src') ?? ''),
  );
  return script ? bundleNameOf(script.getAttribute('src') ?? '') : null;
};

/** How often a tab asks whether it is behind; a build is minutes apart from the next, so once in a while is enough. */
const CHECK_EVERY_MS = 5 * 60 * 1000;
/** Once a mismatch is seen, the confirming check comes sooner than the routine one, so the notice is not five minutes late. */
const RECHECK_AFTER_MISMATCH_MS = 30 * 1000;
/** The first check waits for the page to settle, so it never races the load it is meant to protect. */
const FIRST_CHECK_MS = 8 * 1000;

/** Asks the server which bundle it serves now; null when the answer cannot be read, which is never a reason to nag. */
const latestBundleName = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${window.location.origin}/index.html?build-check=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) {
      return null;
    }
    return bundleNameOf(await response.text());
  } catch {
    return null;
  }
};

/** A deploy in progress can answer with either build for a minute; one mismatch is noise, two in a row is a fact. */
const MISMATCHES_BEFORE_NOTICE = 2;

export default function StaleBuildNotice() {
  const [stale, setStale] = useState(false);
  const mismatches = useRef(0);
  /** One question at a time: two answers to overlapping checks are one observation, not two in a row. */
  const inFlight = useRef(false);

  const check = useCallback(async () => {
    if (inFlight.current) {
      return;
    }
    const running = currentBundleName(document);
    if (!running) {
      return;
    }
    inFlight.current = true;
    let latest: string | null;
    try {
      latest = await latestBundleName();
    } finally {
      inFlight.current = false;
    }
    if (!latest) {
      return;
    }
    // A match clears the count and the notice: a tab that caught up (or a server that rolled back) is not behind.
    mismatches.current = latest === running ? 0 : mismatches.current + 1;
    setStale(mismatches.current >= MISMATCHES_BEFORE_NOTICE);
  }, []);

  useEffect(() => {
    const first = window.setTimeout(() => void check(), FIRST_CHECK_MS);
    const every = window.setInterval(() => void check(), CHECK_EVERY_MS);
    const confirming = window.setInterval(() => {
      if (mismatches.current > 0 && mismatches.current < MISMATCHES_BEFORE_NOTICE) {
        void check();
      }
    }, RECHECK_AFTER_MISMATCH_MS);
    // A tab coming back to the front after a while is the usual way a desk ends up on an old build.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void check();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(first);
      window.clearInterval(every);
      window.clearInterval(confirming);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [check]);

  if (!stale) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1040,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: '8px 16px',
        background: '#1D2433',
        color: '#fff',
        fontSize: 14,
        boxShadow: '0 2px 8px rgba(0,0,0,.25)',
      }}
    >
      <span>A different build of the admin is being served than the one this tab runs. Reload to pick it up.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          font: 'inherit',
          fontWeight: 600,
          padding: '4px 12px',
          borderRadius: 8,
          border: 0,
          background: '#2F6FED',
          color: '#fff',
          cursor: 'pointer',
        }}
      >
        Reload now
      </button>
    </div>
  );
}
